import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Bell, CheckCircle, Trash2, Mail, MailOpen, TestTube } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import notificationManager from '../../utils/notificationManager';

export default function NotificationInbox({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
    checkPushStatus();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      loadNotifications();
      loadUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkPushStatus = () => {
    if (notificationManager.isSupported()) {
      setPushEnabled(notificationManager.getPermission() === 'granted');
    }
  };

  const testPushNotification = () => {
    if (!notificationManager.isSupported()) {
      toast.error('您的瀏覽器不支持推送通知');
      return;
    }

    if (notificationManager.getPermission() !== 'granted') {
      toast.error('請先在設定中啟用推送通知');
      return;
    }

    // Show test notification
    notificationManager.showNotification('測試推送通知', {
      body: '如果您看到這個通知，說明推送功能正常運作！',
      requireInteraction: true,
      vibrate: [200, 100, 200]
    });

    toast.success('測試通知已發送！請查看您的通知欄。');
  };

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error('載入通知失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (error) {
      console.error('載入未讀數失敗:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      toast.error('標記失敗');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const unreadNotifications = notifications.filter(n => !n.is_read);
      
      await Promise.all(
        unreadNotifications.map(n => 
          axios.put(`${API}/notifications/${n.notification_id}/read`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      
      toast.success('已全部標記為已讀');
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return '剛剛';
    if (minutes < 60) return `${minutes}分鐘前`;
    if (hours < 24) return `${hours}小時前`;
    if (days < 7) return `${days}天前`;
    
    return date.toLocaleDateString('zh-TW', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="notification-inbox">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
              <Bell className="text-blue-500" />
              通知收件夾
            </h1>
            {unreadCount > 0 && (
              <p className="text-gray-600 mt-2">
                您有 <span className="text-orange-600 font-bold">{unreadCount}</span> 則未讀通知
              </p>
            )}
          </div>
          
          <div className="flex gap-2">
            {/* Push notification status and test button */}
            {notificationManager.isSupported() && (
              <Button
                onClick={testPushNotification}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <TestTube size={16} />
                測試推送
              </Button>
            )}
            <Button
              onClick={markAllAsRead}
              variant="outline"
              size="sm"
              disabled={unreadCount === 0}
            >
              <CheckCircle size={16} className="mr-2" />
              全部已讀
            </Button>
          </div>
        </div>

        {/* Push notification status banner */}
        {notificationManager.isSupported() && (
          <div className={`mb-4 p-4 rounded-lg border ${
            pushEnabled 
              ? 'bg-green-50 border-green-200' 
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={20} className={pushEnabled ? 'text-green-600' : 'text-yellow-600'} />
                <span className="font-medium">
                  {pushEnabled ? '✅ 推送通知已啟用' : '⚠️ 推送通知未啟用'}
                </span>
              </div>
              {!pushEnabled && (
                <Button
                  onClick={() => window.location.href = '/#/student/settings'}
                  size="sm"
                  variant="outline"
                >
                  前往設定
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {pushEnabled 
                ? '您會在收到新通知時立即收到手機彈出提醒（每5秒檢查一次）' 
                : '啟用後，新通知會立即在手機螢幕上彈出提醒'}
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
            <p className="mt-4 text-gray-600">載入中...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Mail className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">暫無通知</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div 
                key={notification.notification_id}
                className={`bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow ${
                  !notification.is_read ? 'border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${
                    !notification.is_read 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {!notification.is_read ? (
                      <Mail size={24} />
                    ) : (
                      <MailOpen size={24} />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <h3 className={`text-lg font-semibold ${
                        !notification.is_read ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {notification.title}
                      </h3>
                      {!notification.is_read && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                          新
                        </span>
                      )}
                    </div>
                    
                    <p className={`mt-2 ${
                      !notification.is_read ? 'text-gray-700' : 'text-gray-500'
                    }`}>
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gray-500">
                        {formatTime(notification.created_at)}
                      </span>
                      
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notification.notification_id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          標為已讀
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
